import { strings, t, type StringKey } from '../strings';
import { documentName, isNamedDocumentKind, recordKindName } from '../documents/documentNames';
import { REMINDER_SUBJECT_LABELS, reminderSubjectLabel } from '../reminders/schedule';

/**
 * A stored member read in the person's words (decision 0472; batch LBL, T1620).
 * 🔨 The member stays what is posted, filtered and audited; the page draws what
 * this says. A member the lookup has not met falls back to its own words, split
 * and sentence-cased, and never to the raw identifier.
 */
export function plainWords(member: string | null | undefined): string {
  if (member === null || member === undefined) return '';
  const spaced = member
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The member as a lookup key's last segment: lower camel case, `NO_CORPORATE_DOMAIN` as `noCorporateDomain`,
 * `PTOPlanKind` as `ptoPlanKind`, `SalesOrder` as `salesOrder`.
 */
export function memberKey(member: string | null | undefined): string {
  if (member === null || member === undefined) return '';
  if (member.includes('_')) {
    const [first = '', ...rest] = member.toLowerCase().split('_');
    return first + rest.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join('');
  }
  const run = /^([A-Z]+)(?=[A-Z][a-z])/.exec(member)?.[1];
  return run ? run.toLowerCase() + member.slice(run.length) : member.charAt(0).toLowerCase() + member.slice(1);
}

/** The label written under `plain.<group>.<member>`, or the member's split words when none was written. */
export function plainLabel(group: PlainLabelGroup, member: string | null | undefined): string {
  // 🔨 T2047: a missing member is no label, so a page that reads a key the server left out draws nothing here and says None itself, and never crashes.
  if (member === null || member === undefined || member === '') return '';
  const key = `plain.${group}.${memberKey(member)}`;
  // A record kind not written under `plain.recordType` reads as the one name every screen gives it (T2107).
  if (key in strings) return t(key as StringKey);
  return group === 'recordType' ? recordKindName(member) : plainWords(member);
}

/** The groups of members that carry labels, in the lookup's `plain.` keys. */
export const PLAIN_LABEL_GROUPS = ['channel', 'signal', 'signalGroup', 'matchType', 'recordType', 'fieldKind', 'earningSource', 'goalMetric', 'composition'] as const;
export type PlainLabelGroup = (typeof PLAIN_LABEL_GROUPS)[number];

/** A unit family as a person says it: the stored name is run together, ElectricPotential, and reads as Electric potential. */
export function unitFamilyLabel(familyName: string | null | undefined): string {
  return plainWords(familyName);
}

/**
 * What an email template's document type reads as: the document's own name, or the event that sends it,
 * and never the stored word (FileVersionBlocked, OperationFailure).
 */
export function templateTypeLabel(documentType: string | null | undefined): string {
  if (documentType === null || documentType === undefined || documentType === '') return '';
  if (documentType in REMINDER_SUBJECT_LABELS) return reminderSubjectLabel(documentType);
  return isNamedDocumentKind(documentType) ? documentName(documentType) : plainWords(documentType);
}
